import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchCourses, fetchCourseChapters } from '../../api/adminApi';
import DataList from './DataList';
import DataCard from './DataCard';
import Pagination from '../common/Pagination';

const ITEMS_PER_PAGE = 10;

const CoursesSection = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [nestedData, setNestedData] = useState({});

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCourses(currentPage, ITEMS_PER_PAGE, searchTerm);
      setCourses(data.courses || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error loading courses:', err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleToggle = useCallback(async (course) => {
    if (expandedId === course.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(course.id);

    if (nestedData[course.id]) return;

    setNestedData((prev) => ({
      ...prev,
      [course.id]: { loading: true, chapters: [] },
    }));

    try {
      const data = await fetchCourseChapters(course.id);
      setNestedData((prev) => ({
        ...prev,
        [course.id]: { loading: false, chapters: data.chapters || [] },
      }));
    } catch (err) {
      console.error(`Error loading chapters for course ${course.id}:`, err);
      setNestedData((prev) => ({
        ...prev,
        [course.id]: { loading: false, chapters: [] },
      }));
    }
  }, [expandedId, nestedData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedId(null);
  };

  return (
    <div>
      {error && (
        <div className="data-error-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <DataList
        searchPlaceholder="Search courses…"
        searchValue={searchTerm}
        onSearch={handleSearch}
        loading={loading}
        empty={!loading && courses.length === 0}
        emptyMessage={searchTerm ? 'No courses match your search.' : 'No courses found.'}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      >
        {courses.map((course) => {
          const nested = nestedData[course.id];
          const isExpanded = expandedId === course.id;

          return (
            <DataCard
              key={course.id}
              title={course.title}
              subtitle={
                course.description
                  ? course.description.length > 80
                    ? `${course.description.substring(0, 80)}…`
                    : course.description
                  : undefined
              }
              badge={`${course._count?.chapters ?? 0} Chapters`}
              accentColor="var(--accent-green)"
              expanded={isExpanded}
              onToggle={() => handleToggle(course)}
            >
              {nested?.loading ? (
                <div className="data-nested-skeleton">
                  <Skeleton count={3} height={36} borderRadius={6} />
                </div>
              ) : nested?.chapters?.length === 0 ? (
                <div className="data-nested-empty">No chapters in this course.</div>
              ) : (
                <ol className="data-nested-list">
                  {(nested?.chapters || []).map((chapter, idx) => (
                    <li key={chapter.id} className="data-nested-list-item">
                      <span className="data-nested-index">{idx + 1}</span>
                      <div className="data-nested-list-info">
                        <span className="data-nested-list-title">{chapter.name}</span>
                        {chapter.description && (
                          <span className="data-nested-list-sub">{chapter.description}</span>
                        )}
                      </div>
                      <div className="data-nested-list-meta">
                        <span className="data-card-badge" style={{ fontSize: '0.7rem' }}>
                          {chapter._count?.contents ?? 0} Items
                        </span>
                        <span
                          className="data-status-badge"
                          style={{
                            color: chapter.isPublished ? 'var(--accent-green)' : 'var(--text-muted)',
                            backgroundColor: chapter.isPublished
                              ? 'rgba(132,204,22,0.12)'
                              : 'rgba(100,116,139,0.12)',
                          }}
                        >
                          {chapter.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </DataCard>
          );
        })}
      </DataList>
    </div>
  );
};

export default CoursesSection;
